import { useCallback } from 'react';
import { useAuthStore } from '../store/useAuthStore';
import { usePermissionStore } from '../store/usePermissionStore';
import { ROLE_PERMISSIONS } from '../config/rolePermissions';
import type { Permission } from '../config/rolePermissions';

export const usePermission = () => {
  const { user } = useAuthStore();
  const { permissions: backendPermissions, isLoaded } = usePermissionStore();

  const hasPermission = useCallback((permission: Permission | '*') => {
    if (!user) return false;

    // Dashboard is baseline access for every authenticated tenant user.
    if (permission === 'dashboard.view') return true;

    // Owner and SuperAdmin have full access
    if (user.role === 0 || user.role === 5) return true;

    // Prefer backend permissions from bootstrap once loaded. A loaded-but-EMPTY set is
    // authoritative (the backend intentionally revoked everything) — fail closed rather than
    // re-granting role defaults. The static map is only a pre-bootstrap / degraded fallback,
    // reached when isLoaded is false (bootstrap not yet run, or the degraded path left
    // entitlements unknown).
    if (isLoaded) {
      if (backendPermissions.includes('*')) return true;
      return backendPermissions.includes(permission);
    }

    // Fallback: static role-based map (pre-bootstrap / degraded)
    const allowed = ROLE_PERMISSIONS[user.role] || [];
    if ((allowed as readonly (Permission | '*')[]).includes('*')) return true;
    return allowed.includes(permission as Permission);
  }, [user, backendPermissions, isLoaded]);

  return { hasPermission, role: user?.role };
};
